import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { availabilityLocalTimestamp, getApiErrorMessage } from '@moko/utils';
import { notify } from '../Notify';
import { FormActions } from '../FormActions';
import { useFormLayoutStyles } from '../hooks/useFormLayoutStyles';
import type { IWorkAvailabilityServices } from './types';
import { WorkAvailabilityForm } from './WorkAvailabilityForm/WorkAvailabilityForm';
import {
  emptyWorkAvailabilityForm,
  toWorkAvailabilityRange,
  type WorkAvailabilityFormValues,
} from './WorkAvailabilityForm/workAvailabilityFormValues';

export type WorkAvailabilityCreateProps = {
  workId: number;
  services: IWorkAvailabilityServices;
  submitLabel: string;
  onClose: () => void;
  onSaved: () => void;
};

const WorkAvailabilityCreate = ({
  workId,
  services,
  submitLabel,
  onClose,
  onSaved,
}: WorkAvailabilityCreateProps) => {
  const { t } = useTranslation();
  const styles = useFormLayoutStyles();
  const form = useForm<WorkAvailabilityFormValues>({
    defaultValues: emptyWorkAvailabilityForm(),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const { handleSubmit, formState } = form;
  const submitting = formState.isSubmitting;

  const onSubmit = handleSubmit(async (values) => {
    const range = toWorkAvailabilityRange(values);
    if (!range) return;
    try {
      await services.createWorkAvailabilityWindow(new AbortController(), {
        workId,
        startAt: availabilityLocalTimestamp(range.startAt),
        endAt: availabilityLocalTimestamp(range.endAt),
        timezoneId: values.timezoneId,
        priority: values.priority,
        sourceControlled: values.sourceControlled.trim() || undefined,
        status: values.status,
        isAllDay: values.isAllDay,
        duration: values.duration ?? undefined,
      });
      onSaved();
    } catch (err: unknown) {
      notify.error(
        getApiErrorMessage(
          err,
          t('failedToSaveWindow', 'Failed to save availability window.')
        )
      );
    }
  });

  return (
    <FormProvider {...form}>
      <form className={styles.form} onSubmit={onSubmit}>
        <WorkAvailabilityForm disabled={submitting} />
        <FormActions
          disabled={submitting}
          submitting={submitting}
          submitLabel={submitLabel}
          onClose={onClose}
        />
      </form>
    </FormProvider>
  );
};

export default WorkAvailabilityCreate;
